// client/src/pages/WorkWithUsPage.js — public "Work with Business Real Estate" page (/work-with-us).
//
// Everything shown comes from /api/public/recruiting/page: the sections staff edit under
// Administration -> Recruiting -> Page content, the FAQ, testimonials staff entered, and the
// equal-opportunity statement. Empty sections are not shown, so nothing is invented.
//
// The form asks for contact details, licence status and numbers, experience (a count of
// transactions, never income), specialties and a résumé. It never asks for protected-class
// information, pay history or criminal history (see lib/recruiting.js for the citations).
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

const box = 'w-full p-3 border rounded-md';
const blank = {
    first_name: '', last_name: '', email: '', phone: '', licence_status: '', states: [], licence_number_il: '',
    licence_number_fl: '', current_brokerage: '', years_licensed: '', transactions_last_12mo: '', specialties: [],
    heard_about: '', message: '', website: '', consent_calls_texts: false, consent_email_updates: false,
};

const Paragraphs = ({ text }) => String(text || '').split(/\n{2,}/).map((p, i) => (
    <p key={i} className="text-gray-700 leading-relaxed mb-3 whitespace-pre-line">{p}</p>
));

const WorkWithUsPage = () => {
    const [page, setPage] = useState(null);
    const [loadError, setLoadError] = useState('');
    const [f, setF] = useState(blank);
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    useEffect(() => {
        let live = true;
        fetch('/api/public/recruiting/page')
            .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.message || 'Unavailable.'); return j; })
            .then((j) => { if (live) { setPage(j); document.title = `${j.page_title} | ${j.brokerage.name}`; } })
            .catch((e) => live && setLoadError(e.message));
        return () => { live = false; };
    }, []);

    const statusInfo = useMemo(() => (page ? page.form.licence_statuses.find((s) => s.value === f.licence_status) : null), [page, f.licence_status]);
    const licensed = statusInfo && statusInfo.state;
    const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        if (file && page && file.size > page.form.resume.max_mb * 1024 * 1024) { setError(`The résumé must be ${page.form.resume.max_mb} MB or smaller.`); return; }
        setStatus('submitting');
        const fd = new FormData();
        fd.append('site', page.site);
        for (const [k, v] of Object.entries(f)) {
            if (Array.isArray(v)) fd.append(k, v.join(','));
            else if (typeof v === 'boolean') fd.append(k, v ? '1' : '0');
            else fd.append(k, v);
        }
        if (file) fd.append('resume', file);
        try {
            const r = await fetch('/api/public/recruiting/apply', { method: 'POST', body: fd });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || 'Something went wrong.');
            setStatus('success');
        } catch (err) { setError(err.message); setStatus('idle'); }
    };

    if (loadError) return <div className="container mx-auto px-6 py-20 text-center text-gray-700">{loadError}</div>;
    if (!page) return <div className="container mx-auto px-6 py-20 text-center text-gray-500">Loading…</div>;

    return (
        <div className="container mx-auto px-6 py-16">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl font-bold text-center mb-4">{page.page_title}</h1>
                <p className="text-center text-gray-600 mb-10">{page.brokerage.name}{page.brokerage.licence_number ? ` · Licence ${page.brokerage.licence_number}` : ''} · {page.state_name}</p>

                <div className="grid md:grid-cols-2 gap-6 mb-12">
                    {page.sections.map((s) => (
                        <section key={s.key} className={`bg-white p-6 rounded-lg shadow ${s.key === 'intro' ? 'md:col-span-2' : ''}`}>
                            <h2 className="text-xl font-semibold mb-3">{s.title}</h2>
                            <Paragraphs text={s.body} />
                            {s.link && <Link to={s.link.to} className="text-indigo-600 font-semibold hover:underline">{s.link.label} →</Link>}
                        </section>
                    ))}
                </div>

                {page.testimonials.length > 0 && (
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold mb-4 text-center">From our agents</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {page.testimonials.map((t, i) => (
                                <figure key={i} className="bg-white p-6 rounded-lg shadow">
                                    <blockquote className="italic text-gray-700 whitespace-pre-line">“{t.quote}”</blockquote>
                                    <figcaption className="mt-3 text-sm text-gray-500">— {t.attribution}</figcaption>
                                </figure>
                            ))}
                        </div>
                    </section>
                )}

                {page.faq.length > 0 && (
                    <section className="mb-12">
                        <h2 className="text-2xl font-bold mb-4 text-center">Questions</h2>
                        <div className="bg-white rounded-lg shadow divide-y">
                            {page.faq.map((q, i) => (
                                <details key={i} className="p-4">
                                    <summary className="font-semibold cursor-pointer">{q.q}</summary>
                                    <div className="mt-2"><Paragraphs text={q.a} /></div>
                                </details>
                            ))}
                        </div>
                    </section>
                )}

                <section id="apply" className="bg-white p-8 rounded-lg shadow">
                    <h2 className="text-2xl font-bold mb-2">Apply</h2>
                    {status === 'success' ? (
                        <p className="text-lg text-gray-700">Thank you. We have your application, and the managing broker's office will be in touch.</p>
                    ) : !page.accepting_applications ? (
                        <p className="text-gray-700">We are not taking applications through the website right now. Please <Link className="text-indigo-600 underline" to="/contact">contact our office</Link>.</p>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <input type="text" name="website" value={f.website} onChange={set('website')} tabIndex="-1" autoComplete="off"
                                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }} aria-hidden="true" />
                            <div className="grid md:grid-cols-2 gap-4">
                                <label className="block text-sm">First name *<input className={box} value={f.first_name} onChange={set('first_name')} required autoComplete="given-name" /></label>
                                <label className="block text-sm">Last name *<input className={box} value={f.last_name} onChange={set('last_name')} required autoComplete="family-name" /></label>
                                <label className="block text-sm">Email<input className={box} type="email" value={f.email} onChange={set('email')} autoComplete="email" /></label>
                                <label className="block text-sm">Phone<input className={box} type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" /></label>
                            </div>
                            <p className="text-xs text-gray-500">An email or a phone number, so we can reach you.</p>

                            <label className="block text-sm">Licence status *
                                <select className={box} value={f.licence_status} onChange={set('licence_status')} required>
                                    <option value="">Choose…</option>
                                    {page.form.licence_statuses.map((s) => <option key={s.value} value={s.value}>{s.label}{s.state ? ` (${s.state})` : ''}</option>)}
                                </select>
                            </label>

                            <fieldset className="text-sm">
                                <legend className="mb-1">State(s) you want to work in *</legend>
                                <div className="flex gap-6">
                                    {page.form.states.map((s) => (
                                        <label key={s.value} className="flex items-center gap-2"><input type="checkbox" checked={f.states.includes(s.value)} onChange={() => toggle('states', s.value)} />{s.label}</label>
                                    ))}
                                </div>
                            </fieldset>

                            {(licensed || f.states.length > 0) && f.licence_status && !['pre_licence', 'office_staff'].includes(f.licence_status) && (
                                <div className="grid md:grid-cols-2 gap-4">
                                    {(f.states.includes('IL') || licensed === 'IL') && (
                                        <label className="block text-sm">Illinois licence number{licensed === 'IL' ? ' *' : ''}<input className={box} value={f.licence_number_il} onChange={set('licence_number_il')} placeholder="e.g. 475.123456" /></label>
                                    )}
                                    {(f.states.includes('FL') || licensed === 'FL') && (
                                        <label className="block text-sm">Florida licence number{licensed === 'FL' ? ' *' : ''}<input className={box} value={f.licence_number_fl} onChange={set('licence_number_fl')} placeholder="e.g. SL3456789" /></label>
                                    )}
                                </div>
                            )}

                            <div className="grid md:grid-cols-3 gap-4">
                                <label className="block text-sm md:col-span-3">Current brokerage (if any)<input className={box} value={f.current_brokerage} onChange={set('current_brokerage')} /></label>
                                <label className="block text-sm">Years licensed (optional)<input className={box} type="number" min="0" max="70" step="0.5" value={f.years_licensed} onChange={set('years_licensed')} /></label>
                                <label className="block text-sm md:col-span-2">Transactions closed in the last 12 months (optional)<input className={box} type="number" min="0" max="1000" value={f.transactions_last_12mo} onChange={set('transactions_last_12mo')} /></label>
                            </div>

                            <fieldset className="text-sm">
                                <legend className="mb-1">Specialties</legend>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                    {page.form.specialties.map((s) => (
                                        <label key={s} className="flex items-center gap-2"><input type="checkbox" checked={f.specialties.includes(s)} onChange={() => toggle('specialties', s)} />{s}</label>
                                    ))}
                                </div>
                            </fieldset>

                            <label className="block text-sm">How did you hear about us?
                                <select className={box} value={f.heard_about} onChange={set('heard_about')}>
                                    <option value="">Choose…</option>
                                    {page.form.heard_about.map((h) => <option key={h} value={h}>{h}</option>)}
                                </select>
                            </label>
                            <label className="block text-sm">Anything you'd like us to know (optional)<textarea className={box} rows="4" value={f.message} onChange={set('message')} /></label>
                            <label className="block text-sm">Résumé (optional — PDF, Word, RTF or ODT, up to {page.form.resume.max_mb} MB)
                                <input className="block mt-1" type="file" accept={page.form.resume.accept} onChange={(e) => setFile(e.target.files[0] || null)} />
                            </label>

                            <div className="space-y-2 text-xs text-gray-600 border-t pt-4">
                                <label className="flex gap-2 items-start"><input type="checkbox" className="mt-0.5" checked={f.consent_calls_texts} onChange={set('consent_calls_texts')} /><span>{page.form.consents.calls_texts}</span></label>
                                <label className="flex gap-2 items-start"><input type="checkbox" className="mt-0.5" checked={f.consent_email_updates} onChange={set('consent_email_updates')} /><span>{page.form.consents.email_updates}</span></label>
                                <p>We use what you send only to consider your application. We do not ask about pay history or criminal history, and no automated tool scores applications.</p>
                            </div>

                            {error && <p className="text-red-600 text-sm">{error}</p>}
                            <button type="submit" disabled={status === 'submitting'} className="w-full px-8 py-4 text-lg text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:bg-indigo-400">
                                {status === 'submitting' ? 'Sending…' : 'Send my application'}
                            </button>
                        </form>
                    )}
                </section>

                <section className="mt-10 text-sm text-gray-600 bg-slate-50 p-6 rounded-lg">
                    <h2 className="font-semibold mb-2">Equal opportunity</h2>
                    <p className="whitespace-pre-line">{page.eeo}</p>
                </section>
            </div>
        </div>
    );
};

export default WorkWithUsPage;
